import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { canOperateInventory } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { fmtDateTime, variantLabel } from "@/lib/format";
import { ScanReceivePanel, type ScanReceiveLine, type ReceiveLine } from "@/components/feature/ScanReceivePanel";
import * as transfersApi from "@/lib/api/transfers";

interface TransferRow {
  id: string;
  transfer_number: string;
  from_location_id: string;
  to_location_id: string;
  status: string;
  shipped_at: string | null;
  received_at: string | null;
  notes: string | null;
  from_locations?: { name: string } | null;
  to_locations?: { name: string } | null;
}

interface TransferItemRow extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_sent: number;
  qty_received: number;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

export default function TransferDetail() {
  const { id } = useParams();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["transfer-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [transfer, items] = await Promise.all([
        supabase
          .from("stock_transfers")
          .select("id, transfer_number, from_location_id, to_location_id, status, shipped_at, received_at, notes, from_locations:locations!stock_transfers_from_location_id_fkey(name), to_locations:locations!stock_transfers_to_location_id_fkey(name)")
          .eq("org_id", org!.id)
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("stock_transfer_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, product_id, variant_id, qty_sent, qty_received, products(name, sku), variants(sku, option_values)")
          .eq("org_id", org!.id)
          .eq("stock_transfer_id", id)
          .order("created_at"),
      ]);
      return {
        transfer: transfer.data
          ? {
              ...transfer.data,
              from_locations: firstRelation(transfer.data.from_locations),
              to_locations: firstRelation(transfer.data.to_locations),
            } as TransferRow
          : null,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as TransferItemRow[],
      };
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["transfer-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["transfers", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const scanLines: ScanReceiveLine[] = (data?.items ?? []).filter(hasLiveCatalogueIdentity).map((it) => ({
    id: it.id,
    product_id: it.product_id,
    variant_id: it.variant_id,
    name: historicalProductName(it),
    sku: historicalSku(it),
    outstanding: it.qty_sent - it.qty_received,
  }));

  const openReceive = () => {
    setError("");
    if ((data?.items ?? []).some((it) => (it.qty_sent - it.qty_received) > 0 && !hasLiveCatalogueIdentity(it))) {
      setError("A pending line has no live catalogue identity. Reconcile it before receiving stock.");
      return;
    }
    setReceiveOpen(true);
  };

  const commitReceiveLines = async (pLines: ReceiveLine[]) => {
    await transfersApi.receiveStockTransfer(id!, pLines);
  };

  const onReceiveCommitted = () => {
    setReceiveOpen(false);
    invalidate();
  };

  const handleShip = async () => {
    setError("");
    setSaving(true);
    try {
      await transfersApi.shipStockTransfer(id!);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    invalidate();
  };

  const handleCancel = async () => {
    setError("");
    setSaving(true);
    try {
      await transfersApi.cancelStockTransfer(id!);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      setCancelOpen(false);
      return;
    }
    setSaving(false);
    setCancelOpen(false);
    invalidate();
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.transfer) {
    return (
      <div>
        <PageHeader title="Transfer" backTo="/transfers" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this transfer.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const t = data.transfer;
  const sentCount = data.items.reduce((s, it) => s + it.qty_sent, 0);
  const receivedCount = data.items.reduce((s, it) => s + it.qty_received, 0);
  const canShip = t.status === "draft" && canOperateInventory(role);
  const canReceive = ["in_transit", "partial"].includes(t.status) && canOperateInventory(role);
  const canCancel = t.status === "draft" && canOperateInventory(role);

  return (
    <div>
      <PageHeader title={t.transfer_number} subtitle={`${t.from_locations?.name ?? "—"} → ${t.to_locations?.name ?? "—"}`} backTo="/transfers">
        <div className="flex items-center gap-2">
          {canShip && (
            <button
              onClick={handleShip}
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-send-plane-line" /> Ship
            </button>
          )}
          {canReceive && (
            <button
              onClick={openReceive}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-inbox-line" /> Receive
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => setCancelOpen(true)}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-close-circle-line" /> Cancel
            </button>
          )}
        </div>
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <StatusBadge status={t.status} />
              <p className="text-sm font-medium text-slate-800">{receivedCount} / {sentCount} received</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-400">From</p>
                <p className="text-sm font-medium text-slate-800">{t.from_locations?.name ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">To</p>
                <p className="text-sm font-medium text-slate-800">{t.to_locations?.name ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Shipped</p>
                <p className="text-sm font-medium text-slate-800">{fmtDateTime(t.shipped_at)}</p>
              </div>
            </div>
            {t.notes && <p className="text-sm text-slate-600 mt-4">{t.notes}</p>}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Transfer Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sent</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Received</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Remaining</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => {
                    const remaining = it.qty_sent - it.qty_received;
                    const pct = it.qty_sent > 0 ? Math.round((it.qty_received / it.qty_sent) * 100) : 0;
                    return (
                      <tr key={it.id} className="border-b border-slate-50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-800">
                            {historicalProductName(it)}
                            {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                              <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400 font-mono">{historicalSku(it)}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.qty_sent}</td>
                        <td className="px-4 py-3 text-sm text-emerald-700 text-right font-medium">{it.qty_received}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-right">{remaining}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-24 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-slate-400">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        title={`Receive — ${t.transfer_number}`}
        footer={
          <button onClick={() => setReceiveOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Close
          </button>
        }
      >
        <ScanReceivePanel
          docType="stock_transfer"
          docId={id ?? ""}
          orgId={org!.id}
          lines={scanLines}
          commitFn={commitReceiveLines}
          onCommitted={onReceiveCommitted}
        />
      </Modal>

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel Transfer"
        message={`Cancel ${t.transfer_number}? This will not move any stock. This cannot be undone.`}
        confirmLabel="Cancel Transfer"
        loading={saving}
        onCancel={() => setCancelOpen(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
}